import { z } from 'zod';
import {
  meetingActionLinkKindSchema,
  meetingAgendaItemStatusSchema,
  meetingAgendaSourceTypeSchema,
  meetingAttendanceSourceSchema,
  meetingAttendanceStatusSchema,
  meetingAttendeeRoleSchema,
  meetingContentOriginSchema,
  meetingIssueStatusSchema,
  meetingNonWorkingDayRuleSchema,
  meetingOccurrenceStatusSchema,
  meetingSeriesStatusSchema,
  meetingTypeSchema,
  meetingVisibilitySchema,
  calendarNonWorkingDayRuleSchema,
  meetingLobbyPolicySchema,
  ptstdmMeetingTypeSchema,
} from '../enums/meetings';
import {
  CALENDAR_LIMITS,
  effectiveMeetingResponseSchema,
  generalGuestRoleSchema,
  inviteDeliveryModeSchema,
  inviteDeliveryStatusSchema,
  inviteExposureSchema,
  inviteMethodSchema,
  invitePendingReasonSchema,
  inviteSuppressedReasonSchema,
  calendarMailReasonSchema,
  mailFailureReasonSchema,
  meetingDisplayStatusSchema,
  meetingGuestRoleSchema,
  meetingGuestSourceSchema,
  meetingGuestStatusSchema,
  meetingResponseSchema,
  meetingResponseSourceSchema,
  registrationStatusSchema,
} from '../enums/calendar';
import { SCORECARD_LINE_STATUSES } from '../enums/scorecards';
import { richTextDocWireSchema } from '../richtext/schema';
import { taskPrioritySchema } from '../tasks';
import { startCallResponseSchema } from './calls';
import { callMediaSchema } from '../enums/calls';
import {
  emailSchema,
  isoDateTimeSchema,
  localDateSchema,
  multiLineText,
  queryBooleanSchema,
  singleLineText,
  uuidSchema,
} from './common';
import { pageInfoSchema, paginated, paginationQuerySchema } from './pagination';
import { PERFORMANCE_LIMITS, actionBodySchema, trendWeeksQuerySchema } from './performance-common';
import { decimalStringSchema, hhmmSchema } from './ptstdm-common';
import { recurrenceRuleSchema } from './recurrence';
import { createTaskRequestSchema } from './tasks';

/**
 * Meetings (SPEC §7.7, PLAN §4 "Meetings and transcripts", §3.2 "Meetings"; D-850 ff.): agenda
 * templates, series (recurrence, attendees, chair), occurrences, the auto-populated agenda (off-track
 * KPIs first, attendees' open tasks and slips, the last meeting's actions, open decisions,
 * issues; computed live until `start`, then frozen), notes, the decision log, action items (every
 * action item is a task: Proposed to its owner, `source.type = meeting`), attendance, a call or
 * huddle in the linked conversation, the record posted at `conclude`, and meeting health with the
 * derived KPI "meeting action closure". The meetings workstream owns the routes
 * (`apps/api/src/modules/meetings/`); transcripts are in `./transcripts.ts`.
 */

export const meetingSeriesIdParamsSchema = z.object({ seriesId: uuidSchema });
export const meetingOccurrenceIdParamsSchema = z.object({ occurrenceId: uuidSchema });
export const meetingAgendaItemParamsSchema = z.object({
  occurrenceId: uuidSchema,
  itemId: uuidSchema,
});
export const meetingNoteParamsSchema = z.object({ occurrenceId: uuidSchema, noteId: uuidSchema });
export const meetingDecisionParamsSchema = z.object({
  occurrenceId: uuidSchema,
  decisionId: uuidSchema,
});
export const meetingActionItemParamsSchema = z.object({
  occurrenceId: uuidSchema,
  itemId: uuidSchema,
});
export const meetingAttendanceParamsSchema = z.object({
  occurrenceId: uuidSchema,
  userId: uuidSchema,
});
export const meetingIssueParamsSchema = z.object({ seriesId: uuidSchema, issueId: uuidSchema });

// ---------------------------------------------------------------- agenda templates

export const agendaSectionSchema = z.object({
  key: z.string(),
  title: z.string(),
  timeboxMinutes: z.number().int().positive().nullable(),
  /** The `AgendaSource` values that fill the section (empty = manual). */
  autoSources: z.array(meetingAgendaSourceTypeSchema),
});
export const meetingAgendaTemplateDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  meetingType: meetingTypeSchema,
  isSystem: z.boolean(),
  /** `true` for the `dept_tactical` agenda: sections cannot be added, removed or reordered. */
  isLocked: z.boolean(),
  sections: z.array(agendaSectionSchema),
});
export type MeetingAgendaTemplateDto = z.infer<typeof meetingAgendaTemplateDtoSchema>;
export const meetingAgendaTemplateListResponseSchema = z.object({
  items: z.array(meetingAgendaTemplateDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});

// ---------------------------------------------------------------- series

export const meetingAttendeeDtoSchema = z.object({
  userId: uuidSchema,
  role: meetingAttendeeRoleSchema,
});

export const meetingSeriesDtoSchema = z.object({
  id: uuidSchema,
  type: meetingTypeSchema,
  title: z.string(),
  description: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
  /** The report of a 1:1. */
  subjectUserId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  chairUserId: uuidSchema,
  noteTakerUserId: uuidSchema.nullable(),
  /** Null for a one-off meeting. */
  recurrence: recurrenceRuleSchema.nullable(),
  dtstartDate: localDateSchema,
  startTime: hhmmSchema,
  durationMinutes: z.number().int(),
  nonWorkingDayRule: meetingNonWorkingDayRuleSchema,
  agendaTemplateId: uuidSchema,
  sectionTimeboxes: z.record(z.string(), z.number().int().positive()).nullable(),
  linkedConversationId: uuidSchema,
  visibility: meetingVisibilitySchema,
  onTimeGraceMinutes: z.number().int(),
  generateAheadDays: z.number().int(),
  status: meetingSeriesStatusSchema,
  attendees: z.array(meetingAttendeeDtoSchema),
  /**
   * Wave 5 (D-1002, D-1026, D-1030): calendar-type fields. The meetings stream sets them on every
   * series it returns; they are optional so that a series built before wave 5 still validates.
   */
  location: z.string().nullable().optional(),
  online: z.boolean().optional(),
  lobbyPolicy: meetingLobbyPolicySchema.optional(),
  guestsCanPresent: z.boolean().optional(),
  /** The booking page a booked meeting came from ("Booked through <page>", D-1035). */
  bookedThrough: z.object({ pageId: uuidSchema, title: z.string() }).nullable().optional(),
  nextOccurrenceId: uuidSchema.nullable(),
  nextStartsAt: isoDateTimeSchema.nullable(),
  createdByUserId: uuidSchema,
  endedAt: isoDateTimeSchema.nullable(),
  version: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type MeetingSeriesDto = z.infer<typeof meetingSeriesDtoSchema>;
export const meetingSeriesResponseSchema = z.object({ series: meetingSeriesDtoSchema });

export const listMeetingSeriesQuerySchema = paginationQuerySchema.extend({
  type: meetingTypeSchema.optional(),
  status: meetingSeriesStatusSchema.optional(),
  departmentId: uuidSchema.optional(),
  /** Only series the caller attends or chairs. */
  mine: queryBooleanSchema.optional(),
});
export const meetingSeriesListResponseSchema = paginated(meetingSeriesDtoSchema);

/**
 * `POST /meeting-series` (manager+). A `one_on_one` needs `subjectUserId` and has exactly the
 * chair and the subject; `dept_tactical` needs `departmentId`. A one-off meeting has no
 * `recurrence`. `linkedConversationId` is where the meeting record is posted; for a 1:1 the
 * chair–subject DM is used (and created if missing). Only the six PTSTDM types: calendar meetings
 * come from `POST /meetings` and `POST /webinars` (D-1002).
 */
export const createMeetingSeriesRequestSchema = z.strictObject({
  title: singleLineText(1, PERFORMANCE_LIMITS.nameMax),
  type: ptstdmMeetingTypeSchema,
  description: multiLineText(1, PERFORMANCE_LIMITS.descriptionMax).optional(),
  recurrence: recurrenceRuleSchema.nullable().default(null),
  dtstartDate: localDateSchema,
  startTime: hhmmSchema,
  durationMinutes: z.int().min(5).max(480),
  attendeeUserIds: z.array(uuidSchema).max(PERFORMANCE_LIMITS.meetingAttendeesMax).default([]),
  chairUserId: uuidSchema,
  noteTakerUserId: uuidSchema.nullable().optional(),
  subjectUserId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  linkedConversationId: uuidSchema.optional(),
  /** Default: the system template of the type. */
  agendaTemplateId: uuidSchema.optional(),
  /** `{ sectionKey: minutes }`. */
  sectionTimeboxes: z.record(z.string(), z.int().min(1).max(240)).optional(),
  nonWorkingDayRule: meetingNonWorkingDayRuleSchema.optional(),
  visibility: meetingVisibilitySchema.optional(),
  onTimeGraceMinutes: z.int().min(0).max(60).optional(),
  generateAheadDays: z.int().min(1).max(60).optional(),
});
export type CreateMeetingSeriesRequest = z.input<typeof createMeetingSeriesRequestSchema>;

/** `PATCH /meeting-series/:seriesId` (the chair or admin+; future scheduled occurrences follow). */
export const updateMeetingSeriesRequestSchema = z.strictObject({
  title: createMeetingSeriesRequestSchema.shape.title.optional(),
  description: multiLineText(1, PERFORMANCE_LIMITS.descriptionMax).nullable().optional(),
  recurrence: recurrenceRuleSchema.nullable().optional(),
  dtstartDate: localDateSchema.optional(),
  startTime: hhmmSchema.optional(),
  durationMinutes: z.int().min(5).max(480).optional(),
  attendeeUserIds: z.array(uuidSchema).max(PERFORMANCE_LIMITS.meetingAttendeesMax).optional(),
  chairUserId: uuidSchema.optional(),
  noteTakerUserId: uuidSchema.nullable().optional(),
  linkedConversationId: uuidSchema.optional(),
  agendaTemplateId: uuidSchema.optional(),
  sectionTimeboxes: z.record(z.string(), z.int().min(1).max(240)).nullable().optional(),
  nonWorkingDayRule: meetingNonWorkingDayRuleSchema.optional(),
  visibility: meetingVisibilitySchema.optional(),
  onTimeGraceMinutes: z.int().min(0).max(60).optional(),
  generateAheadDays: z.int().min(1).max(60).optional(),
  /** `true` ends the series (status `ended`), `false` resumes a paused or ended one. */
  archived: z.boolean().optional(),
  paused: z.boolean().optional(),
  // Wave 5 — calendar types only (D-1026, D-1030, D-1049). Members by role replace the attendee
  // list; `status: 'ended'` ends the series (UNTIL or CANCEL per D-1018). A time change of a one-off
  // answers `422 edit_the_occurrence`.
  location: singleLineText(1, 300).nullable().optional(),
  online: z.boolean().optional(),
  lobbyPolicy: meetingLobbyPolicySchema.optional(),
  guestsCanPresent: z.boolean().optional(),
  requiredUserIds: z.array(uuidSchema).max(PERFORMANCE_LIMITS.meetingAttendeesMax).optional(),
  optionalUserIds: z.array(uuidSchema).max(PERFORMANCE_LIMITS.meetingAttendeesMax).optional(),
  status: z.literal('ended').optional(),
});

// ---------------------------------------------------------------- occurrences

export const meetingOccurrenceDtoSchema = z.object({
  id: uuidSchema,
  seriesId: uuidSchema,
  title: z.string(),
  type: meetingTypeSchema,
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  /** The workspace-local date of `startsAt`. */
  localDate: localDateSchema,
  status: meetingOccurrenceStatusSchema,
  chairUserId: uuidSchema,
  linkedConversationId: uuidSchema,
  callId: uuidSchema.nullable(),
  startedOnTime: z.boolean().nullable(),
  actualStartedAt: isoDateTimeSchema.nullable(),
  actualEndedAt: isoDateTimeSchema.nullable(),
  cancelReason: z.string().nullable(),
  /** The meeting record post in the linked channel, once concluded. */
  recordMessageId: uuidSchema.nullable(),
  agendaGeneratedAt: isoDateTimeSchema.nullable(),
  agendaFrozenAt: isoDateTimeSchema.nullable(),
  attendanceExpected: z.number().int().nullable(),
  attendancePresent: z.number().int().nullable(),
  /**
   * Wave 5 (D-1005, D-1007, D-1008, D-1014): set by the meetings stream on every occurrence it
   * returns; optional so that a payload built before wave 5 still validates.
   * - `nominalDate`: the recurrence date before any move (the identity of RSVP, reminders, UIDs);
   * - `description` and `location`: the series' text or this occurrence's override;
   * - `isException`: "just this one" was edited; `recurring`: the series repeats;
   * - `guestCount`: active outside guests; `myResponse`: the caller's effective answer (null when
   *   the caller is neither participant nor audience);
   * - `displayStatus`: `ended` for a past calendar-type occurrence still `scheduled` or `in_progress`.
   */
  nominalDate: localDateSchema.optional(),
  description: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  online: z.boolean().optional(),
  isException: z.boolean().optional(),
  recurring: z.boolean().optional(),
  guestCount: z.number().int().nonnegative().optional(),
  myResponse: effectiveMeetingResponseSchema.nullable().optional(),
  displayStatus: meetingDisplayStatusSchema.optional(),
  version: z.number().int().positive(),
});
export type MeetingOccurrenceDto = z.infer<typeof meetingOccurrenceDtoSchema>;
export const meetingOccurrenceResponseSchema = z.object({ occurrence: meetingOccurrenceDtoSchema });

/** `GET /meeting-occurrences`: the morning agenda uses `mine=true` with today's date. */
/** The largest page of `GET /meeting-occurrences` (and its default, D-997). */
export const MEETING_OCCURRENCES_PAGE_MAX = 500;

/**
 * `GET /meeting-occurrences`: the meetings in `[from, to]` (at most 400 days), oldest first, in
 * pages of `limit` (D-997): follow `nextCursor` (issued for these filters only) until it is null.
 */
export const listMeetingOccurrencesQuerySchema = z.object({
  from: localDateSchema,
  to: localDateSchema,
  mine: queryBooleanSchema.optional(),
  seriesId: uuidSchema.optional(),
  status: meetingOccurrenceStatusSchema.optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MEETING_OCCURRENCES_PAGE_MAX)
    .default(MEETING_OCCURRENCES_PAGE_MAX),
});
export const meetingOccurrenceListResponseSchema = paginated(meetingOccurrenceDtoSchema);

/** `POST /meeting-occurrences`: an ad-hoc meeting (member+). */
export const createAdHocMeetingRequestSchema = z.strictObject({
  title: singleLineText(1, PERFORMANCE_LIMITS.nameMax),
  startsAt: isoDateTimeSchema,
  durationMinutes: z.int().min(5).max(480),
  attendeeUserIds: z.array(uuidSchema).min(1).max(PERFORMANCE_LIMITS.meetingAttendeesMax),
  linkedConversationId: uuidSchema.optional(),
});

export const startMeetingRequestSchema = actionBodySchema;
export const concludeMeetingRequestSchema = actionBodySchema;
export const refreshMeetingAgendaRequestSchema = actionBodySchema;
/** A cancelled occurrence keeps its reason. */
export const cancelMeetingRequestSchema = z.strictObject({
  reason: singleLineText(1, 500),
});

// ---------------------------------------------------------------- the auto-populated agenda

const taskMiniSchema = z.object({
  taskId: uuidSchema,
  /** `T-1042`. */
  number: z.string(),
  title: z.string(),
  status: z.string(),
  priority: taskPrioritySchema,
  ownerUserId: uuidSchema,
  dueDate: localDateSchema.nullable(),
});
export const meetingTaskMiniSchema = taskMiniSchema;

export const meetingAgendaKpiSchema = z.object({
  kpiId: uuidSchema,
  name: z.string(),
  /** The person (1:1) or department the KPI belongs to. */
  userId: uuidSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  status: z.enum(SCORECARD_LINE_STATUSES),
  target: decimalStringSchema.nullable(),
  mtdActual: decimalStringSchema.nullable(),
  achievedPct: decimalStringSchema.nullable(),
});

export const meetingIssueDtoSchema = z.object({
  id: uuidSchema,
  seriesId: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  priority: z.number().int().min(1).max(3).nullable(),
  raisedByUserId: uuidSchema,
  raisedAt: isoDateTimeSchema,
  sourceMessageId: uuidSchema.nullable(),
  status: meetingIssueStatusSchema,
  resolvedInOccurrenceId: uuidSchema.nullable(),
  resolutionNote: z.string().nullable(),
  resolvedByUserId: uuidSchema.nullable(),
  resolvedAt: isoDateTimeSchema.nullable(),
});
export type MeetingIssueDto = z.infer<typeof meetingIssueDtoSchema>;

/** The auto-population of `GET /meeting-occurrences/:occurrenceId` (each item rendered per viewer). */
export const meetingAgendaSourcesSchema = z.object({
  /** Off-track first, then by % achieved ascending. */
  offTrackKpis: z.array(meetingAgendaKpiSchema),
  openTasksByAttendee: z.array(
    z.object({
      userId: uuidSchema,
      tasks: z.array(taskMiniSchema),
      slips: z.array(taskMiniSchema.extend({ slipCount: z.number().int().nonnegative() })),
    }),
  ),
  lastActionItems: z.array(z.object({ task: taskMiniSchema, status: z.string() })),
  openDecisions: z.array(taskMiniSchema),
  issues: z.array(meetingIssueDtoSchema),
  /** 1:1: the subject's latest weekly review. */
  weeklyReview: z
    .object({
      reviewId: uuidSchema,
      weekStart: localDateSchema,
      sayDoRatio: decimalStringSchema.nullable(),
    })
    .nullable(),
  /** Leadership and monthly types: one row per department. */
  departmentHealth: z.array(
    z.object({
      departmentId: uuidSchema,
      name: z.string(),
      sayDoRatio: decimalStringSchema.nullable(),
      overdueCount: z.number().int().nonnegative(),
      reportCompliancePct: decimalStringSchema.nullable(),
      planVsActualPct: decimalStringSchema.nullable(),
    }),
  ),
  meetingHealth: z
    .object({
      heldOnTimePct: decimalStringSchema.nullable(),
      actionClosurePct: decimalStringSchema.nullable(),
    })
    .nullable(),
});
export type MeetingAgendaSources = z.infer<typeof meetingAgendaSourcesSchema>;

export const meetingAgendaItemDtoSchema = z.object({
  id: uuidSchema,
  sectionKey: z.string(),
  position: z.number().int().nonnegative(),
  title: z.string(),
  sourceType: meetingAgendaSourceTypeSchema,
  sourceRefId: uuidSchema.nullable(),
  /** The values at generation, refreshed until the agenda is frozen. */
  sourceSnapshot: z.record(z.string(), z.unknown()).nullable(),
  presenterUserId: uuidSchema.nullable(),
  status: meetingAgendaItemStatusSchema,
  /** `status` is `discussed` or `resolved`. */
  done: z.boolean(),
  addedByUserId: uuidSchema.nullable(),
});
export type MeetingAgendaItemDto = z.infer<typeof meetingAgendaItemDtoSchema>;
export const meetingAgendaItemResponseSchema = z.object({ item: meetingAgendaItemDtoSchema });

export const createMeetingAgendaItemRequestSchema = z.strictObject({
  title: singleLineText(1, 300),
  /** A section key of the series' template; default `manual`'s own first section. */
  section: z.string().trim().min(1).max(60).optional(),
  position: z.int().min(0).max(1_000).optional(),
  presenterUserId: uuidSchema.nullable().optional(),
});
export const updateMeetingAgendaItemRequestSchema = z.strictObject({
  title: singleLineText(1, 300).optional(),
  section: z.string().trim().min(1).max(60).optional(),
  position: z.int().min(0).max(1_000).optional(),
  /** Tick or untick an item. */
  done: z.boolean().optional(),
  status: meetingAgendaItemStatusSchema.optional(),
  presenterUserId: uuidSchema.nullable().optional(),
});

// ---------------------------------------------------------------- notes, decisions, action items, attendance

const noteBodySchema = richTextDocWireSchema;

export const meetingNoteDtoSchema = z.object({
  id: uuidSchema,
  agendaItemId: uuidSchema.nullable(),
  authorUserId: uuidSchema,
  body: noteBodySchema,
  text: z.string(),
  origin: meetingContentOriginSchema,
  aiDraftId: uuidSchema.nullable(),
  version: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type MeetingNoteDto = z.infer<typeof meetingNoteDtoSchema>;
export const meetingNoteResponseSchema = z.object({ note: meetingNoteDtoSchema });

/** The server checks at most 50,000 characters of text (`PERFORMANCE_LIMITS.meetingNoteTextMax`). */
export const createMeetingNoteRequestSchema = z.strictObject({
  body: noteBodySchema,
  agendaItemId: uuidSchema.optional(),
});
/** `409 draft_stale` when `clientUpdatedAt` is older than the note's `updatedAt`. */
export const updateMeetingNoteRequestSchema = z.strictObject({
  body: noteBodySchema,
  clientUpdatedAt: isoDateTimeSchema,
});

export const meetingDecisionDtoSchema = z.object({
  id: uuidSchema,
  agendaItemId: uuidSchema.nullable(),
  text: z.string(),
  rationale: z.string().nullable(),
  decidedByUserId: uuidSchema,
  resolvesTaskId: uuidSchema.nullable(),
  resolvesIssueId: uuidSchema.nullable(),
  origin: meetingContentOriginSchema,
  aiDraftId: uuidSchema.nullable(),
  recordedByUserId: uuidSchema,
  recordedAt: isoDateTimeSchema,
  /** Decisions are revised by superseding them, never edited in place after conclude. */
  supersededById: uuidSchema.nullable(),
});
export type MeetingDecisionDto = z.infer<typeof meetingDecisionDtoSchema>;
export const meetingDecisionResponseSchema = z.object({ decision: meetingDecisionDtoSchema });

/** `POST/PATCH …/decisions` (the chair). */
export const createMeetingDecisionRequestSchema = z.strictObject({
  text: multiLineText(1, 2_000),
  rationale: multiLineText(1, 2_000).optional(),
  decidedByUserId: uuidSchema.optional(),
  agendaItemId: uuidSchema.optional(),
  /** A decision request this resolves (the `decide` transition runs for its owner). */
  resolvesTaskId: uuidSchema.optional(),
  /** An issue this solves. */
  resolvesIssueId: uuidSchema.optional(),
});
export const updateMeetingDecisionRequestSchema = createMeetingDecisionRequestSchema.partial();

export const meetingActionItemDtoSchema = z.object({
  id: uuidSchema,
  agendaItemId: uuidSchema.nullable(),
  taskId: uuidSchema,
  linkKind: meetingActionLinkKindSchema,
  createdByUserId: uuidSchema,
  task: taskMiniSchema,
});
export type MeetingActionItemDto = z.infer<typeof meetingActionItemDtoSchema>;
export const meetingActionItemResponseSchema = z.object({
  actionItem: meetingActionItemDtoSchema,
});

/**
 * `POST …/action-items` (an attendee): a task as for `POST /tasks` (the server sets
 * `source.type = meeting`, `createdVia = meeting`; the upward guard and the Proposed flow apply),
 * or an existing task to link (`linkTaskId`).
 */
export const createMeetingActionItemRequestSchema = z.union([
  createTaskRequestSchema
    .omit({ source: true, createdVia: true })
    .extend({ agendaItemId: uuidSchema.optional() }),
  z.strictObject({ linkTaskId: uuidSchema, agendaItemId: uuidSchema.optional() }),
]);
/**
 * `POST …/action-items` headers: an optional `Idempotency-Key`, as for `POST /tasks` (the same
 * format; a retry with the same key answers the action item it made, `200` with
 * `idempotent-replayed: true`, and creates no second task).
 */
export const createMeetingActionItemHeadersSchema = z.looseObject({
  'idempotency-key': z
    .string()
    .regex(/^[A-Za-z0-9_-]{8,64}$/)
    .optional(),
});
/** `PATCH …/action-items/:itemId`: file the item under another agenda item (or none). */
export const updateMeetingActionItemRequestSchema = z.strictObject({
  agendaItemId: uuidSchema.nullable(),
});

export const meetingAttendanceDtoSchema = z.object({
  userId: uuidSchema,
  expected: z.boolean(),
  status: meetingAttendanceStatusSchema,
  joinedAt: isoDateTimeSchema.nullable(),
  leftAt: isoDateTimeSchema.nullable(),
  source: meetingAttendanceSourceSchema,
  markedByUserId: uuidSchema.nullable(),
});
export type MeetingAttendanceDto = z.infer<typeof meetingAttendanceDtoSchema>;
export const meetingAttendanceResponseSchema = z.object({ attendance: meetingAttendanceDtoSchema });
/** `PUT …/attendance/:userId` (the chair). */
export const putMeetingAttendanceRequestSchema = z.strictObject({
  status: z.enum(['present', 'late', 'absent', 'excused']),
});

/** `GET /meeting-occurrences/:occurrenceId`: the meeting with its agenda and everything recorded. */
export const meetingOccurrenceDetailResponseSchema = z.object({
  occurrence: meetingOccurrenceDtoSchema,
  series: z.object({
    id: uuidSchema,
    title: z.string(),
    type: meetingTypeSchema,
    agendaTemplateId: uuidSchema,
    departmentId: uuidSchema.nullable(),
    subjectUserId: uuidSchema.nullable(),
  }),
  agenda: meetingAgendaSourcesSchema,
  agendaItems: z.array(meetingAgendaItemDtoSchema),
  notes: z.array(meetingNoteDtoSchema),
  decisions: z.array(meetingDecisionDtoSchema),
  actionItems: z.array(meetingActionItemDtoSchema),
  attendance: z.array(meetingAttendanceDtoSchema),
  /** `scheduled` before `start`: computed live; afterwards the frozen copy. */
  agendaFrozen: z.boolean(),
});
export type MeetingOccurrenceDetailResponse = z.infer<typeof meetingOccurrenceDetailResponseSchema>;

// ---------------------------------------------------------------- issues list

export const listMeetingIssuesQuerySchema = z.object({
  status: meetingIssueStatusSchema.optional(),
});
export const meetingIssueListResponseSchema = z.object({
  items: z.array(meetingIssueDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});
export const meetingIssueResponseSchema = z.object({ issue: meetingIssueDtoSchema });
export const createMeetingIssueRequestSchema = z.strictObject({
  title: singleLineText(1, 300),
  description: multiLineText(1, 2_000).optional(),
  priority: z.int().min(1).max(3).optional(),
  sourceMessageId: uuidSchema.optional(),
});
export const updateMeetingIssueRequestSchema = z.strictObject({
  title: singleLineText(1, 300).optional(),
  description: multiLineText(1, 2_000).nullable().optional(),
  priority: z.int().min(1).max(3).nullable().optional(),
  status: meetingIssueStatusSchema.optional(),
  resolutionNote: multiLineText(1, 2_000).optional(),
  resolvedInOccurrenceId: uuidSchema.optional(),
});

// ---------------------------------------------------------------- call, health

/** `POST /meeting-occurrences/:occurrenceId/call` (`422 no_linked_conversation`). */
export const startMeetingCallRequestSchema = z.strictObject({
  kind: z.enum(['call', 'huddle']),
  media: callMediaSchema.optional(),
});
/** As `POST /calls` and the huddle join: the call and the caller's LiveKit grant. */
export const startMeetingCallResponseSchema = startCallResponseSchema.extend({
  occurrence: meetingOccurrenceDtoSchema,
});

export const meetingHealthQuerySchema = z.object({ weeks: trendWeeksQuerySchema });
export const meetingHealthResponseSchema = z.object({
  seriesId: uuidSchema,
  weeks: z.union([z.literal(4), z.literal(12)]),
  heldOnTimePct: decimalStringSchema.nullable(),
  heldPct: decimalStringSchema.nullable(),
  actionItemsClosedByNextMeetingPct: decimalStringSchema.nullable(),
  trend: z.array(
    z.object({
      weekStart: localDateSchema,
      scheduled: z.number().int().nonnegative(),
      held: z.number().int().nonnegative(),
      heldOnTime: z.number().int().nonnegative(),
      actionItems: z.number().int().nonnegative(),
      actionItemsClosedByNext: z.number().int().nonnegative(),
    }),
  ),
});

// ---------------------------------------------------------------- settings (`meetings.*`)

/** `GET/PATCH /admin/settings/meetings` (admin+). */
export const meetingSettingsSchema = z.object({
  /** Who may write meeting notes: the chair and note taker only, or every attendee (default). */
  notesEditableBy: z.enum(['chair_and_note_taker', 'attendees']),
});
export type MeetingSettingsDto = z.infer<typeof meetingSettingsSchema>;
export const updateMeetingSettingsRequestSchema = z
  .strictObject({ notesEditableBy: meetingSettingsSchema.shape.notesEditableBy.optional() })
  .refine((value) => Object.keys(value).length > 0, 'Send at least one setting');

// ================================================================ wave 5 — calendar meetings (D-1000 ff.)
// Routes of the meetings module added by wave 5 (brief §6.1). The meetings stream implements them on
// core's kernels (`services/meetings/schedule.ts`, `occurrence-kernel.ts`, `guests.ts`,
// `responses.ts`).

export const meetingGuestParamsSchema = z.object({ seriesId: uuidSchema, guestId: uuidSchema });

/** The mail state a scheduling response reports (D-1021): "Email isn't set up on this server yet". */
export const calendarMailStateSchema = z.object({
  deliverable: z.boolean(),
  reason: calendarMailReasonSchema,
});
export type CalendarMailState = z.infer<typeof calendarMailStateSchema>;

/** A guest's name from an organiser (no CR/LF, 1..120, D-1011). */
export const guestDisplayNameSchema = singleLineText(1, 120);
/** A company, optional. */
export const guestCompanySchema = singleLineText(1, 120);

/**
 * One outside guest as the organiser adds them: with an email (invitations by mail), or name-only
 * as a "link-only" guest whose personal link the organiser copies and sends any way they like.
 */
export const meetingGuestInputSchema = z.strictObject({
  email: emailSchema.optional(),
  name: guestDisplayNameSchema,
  company: guestCompanySchema.optional(),
  role: generalGuestRoleSchema.default('required'),
});
export type MeetingGuestInput = z.input<typeof meetingGuestInputSchema>;

/** Where a meeting is held. */
export const meetingLocationSchema = singleLineText(1, 300);

/**
 * `POST /meetings` (`calendar.use`): a `general` meeting with members by role and outside guests
 * (D-1011, D-1026). A channel in `linkedConversationId` makes the meeting channel-visible (every
 * channel member sees it and may join); a DM or group DM the caller belongs to hosts an
 * attendees-only meeting; without one the host is the people's DM or group DM, else the
 * organiser's self DM. There is no separate visibility field. Guests need
 * `calendar.externalGuestsEnabled`; a recurring series with guests needs a rule iCalendar can
 * express (`422 recurrence_not_shareable`). `Idempotency-Key` as for every creating POST.
 */
export const scheduleMeetingRequestSchema = z.strictObject({
  type: z.literal('general'),
  title: singleLineText(1, 200),
  description: multiLineText(1, 4_000).optional(),
  startsAt: isoDateTimeSchema,
  durationMinutes: z.int().min(5).max(480),
  recurrence: recurrenceRuleSchema.nullable().default(null),
  nonWorkingDayRule: calendarNonWorkingDayRuleSchema.default('keep'),
  requiredUserIds: z.array(uuidSchema).max(PERFORMANCE_LIMITS.meetingAttendeesMax).default([]),
  optionalUserIds: z.array(uuidSchema).max(PERFORMANCE_LIMITS.meetingAttendeesMax).default([]),
  guests: z.array(meetingGuestInputSchema).max(CALENDAR_LIMITS.maxGuestsPerMeeting).default([]),
  location: meetingLocationSchema.optional(),
  online: z.boolean().default(true),
  linkedConversationId: uuidSchema.optional(),
  options: z
    .strictObject({
      lobbyPolicy: meetingLobbyPolicySchema.default('guests_wait'),
      guestsCanPresent: z.boolean().default(true),
    })
    .default({ lobbyPolicy: 'guests_wait', guestsCanPresent: true }),
});
export type ScheduleMeetingRequest = z.input<typeof scheduleMeetingRequestSchema>;

/** The per-recipient invitation state an organiser sees (D-1018, D-1021, D-1022). */
export const inviteDeliveryStateSchema = z.object({
  status: inviteDeliveryStatusSchema,
  pendingReason: invitePendingReasonSchema.nullable(),
  suppressedReason: inviteSuppressedReasonSchema.nullable(),
  failureReason: mailFailureReasonSchema.nullable(),
  mode: inviteDeliveryModeSchema,
  nominalDate: localDateSchema.nullable(),
  lastSequence: z.number().int().nonnegative().nullable(),
  lastMethod: inviteMethodSchema.nullable(),
  lastExposure: inviteExposureSchema.nullable(),
  updatedAt: isoDateTimeSchema,
});
export type InviteDeliveryState = z.infer<typeof inviteDeliveryStateSchema>;

/**
 * An outside guest of a series. Organisers (chair, occurrence chair; webinar presenters for
 * registrations) see `email`, `delivery`, `answers` and the link state; participants see names
 * only (`email` and `delivery` null, D-1011); the webinar audience sees nothing (403).
 */
export const meetingGuestDtoSchema = z.object({
  id: uuidSchema,
  seriesId: uuidSchema,
  name: z.string(),
  email: z.string().nullable(),
  company: z.string().nullable(),
  role: meetingGuestRoleSchema,
  source: meetingGuestSourceSchema,
  status: meetingGuestStatusSchema,
  registrationStatus: registrationStatusSchema.nullable(),
  /** No email: the organiser shares the personal link themselves. */
  linkOnly: z.boolean(),
  /** The series-level effective answer. */
  response: effectiveMeetingResponseSchema,
  /** Organisers only: the latest delivery row (series or next occurrence), else null. */
  delivery: inviteDeliveryStateSchema.nullable(),
  /** Organisers only: the link can be shown again with the current key (D-1012). */
  linkReshowable: z.boolean().nullable(),
  /** Retention already anonymised the row ("Former guest"). */
  anonymized: z.boolean(),
  createdAt: isoDateTimeSchema,
});
export type MeetingGuestDto = z.infer<typeof meetingGuestDtoSchema>;

/** A guest the organiser just added, with the link to copy (shown to organisers only). */
export const addedMeetingGuestSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  email: z.string().nullable(),
  /** `https://<HUB_PUBLIC_URL>/m#t=<token>` (D-1013). */
  personalUrl: z.string(),
  delivery: inviteDeliveryStateSchema.nullable(),
});
export type AddedMeetingGuest = z.infer<typeof addedMeetingGuestSchema>;

/**
 * An email that belongs to an active non-guest member became a member attendee instead of a guest
 * (D-1011, A3b). Registration and booking never convert.
 */
export const convertedGuestSchema = z.object({
  email: z.string(),
  userId: uuidSchema,
  role: generalGuestRoleSchema,
});

/** `201` of `POST /meetings`. */
export const scheduleMeetingResponseSchema = z.object({
  series: meetingSeriesDtoSchema,
  occurrence: meetingOccurrenceDtoSchema,
  guests: z.array(addedMeetingGuestSchema),
  convertedToMembers: z.array(convertedGuestSchema),
  mail: calendarMailStateSchema,
});
export type ScheduleMeetingResponse = z.infer<typeof scheduleMeetingResponseSchema>;

/**
 * `PATCH /meeting-occurrences/:occurrenceId`: "just this one" (D-1008) — a reschedule, title,
 * description and location overrides, or a substitute chair. Only future `scheduled` occurrences;
 * the new local date stays strictly between the neighbouring live occurrences
 * (`422 reschedule_out_of_order`). `version` is the occurrence's (`412 version_conflict`).
 */
export const updateMeetingOccurrenceRequestSchema = z
  .strictObject({
    startsAt: isoDateTimeSchema.optional(),
    durationMinutes: z.int().min(5).max(480).optional(),
    title: singleLineText(1, 200).nullable().optional(),
    description: multiLineText(1, 4_000).nullable().optional(),
    location: meetingLocationSchema.nullable().optional(),
    chairUserId: uuidSchema.optional(),
    version: z.int().positive(),
  })
  .refine((body) => Object.keys(body).some((key) => key !== 'version'), 'Send at least one change');

/**
 * `POST /meeting-series/:seriesId/occurrences` (participants): materialise the occurrence of a
 * planned nominal date (≤ now + 400 days) to open, edit or join it. Idempotent: an existing one is
 * returned (`200`), a new one `201`.
 */
export const materialiseOccurrenceRequestSchema = z.strictObject({ nominalDate: localDateSchema });

/**
 * `PUT /meeting-series/:seriesId/responses/me` (participants and audience, D-1014): answer the
 * whole series (`nominalDate: null`) or one occurrence. RSVP never writes attendance.
 */
export const respondToMeetingRequestSchema = z.strictObject({
  nominalDate: localDateSchema.nullable(),
  response: meetingResponseSchema,
  note: multiLineText(1, 500).optional(),
});

/** The answer counts of a series or occurrence (also in `meeting.responses.updated`). */
export const meetingResponseTalliesSchema = z.object({
  accepted: z.number().int().nonnegative(),
  tentative: z.number().int().nonnegative(),
  declined: z.number().int().nonnegative(),
  needsAction: z.number().int().nonnegative(),
});
export type MeetingResponseTallies = z.infer<typeof meetingResponseTalliesSchema>;

export const meetingResponseDtoSchema = z.object({
  /** `member` (a participant or audience member) or an outside `guest`. */
  kind: z.enum(['member', 'guest']),
  userId: uuidSchema.nullable(),
  guestId: uuidSchema.nullable(),
  name: z.string(),
  /** Guests' emails for organisers only (null otherwise and for members). */
  email: z.string().nullable(),
  role: z.union([meetingGuestRoleSchema, z.enum(['chair'])]),
  /** The effective answer for `nominalDate` (occurrence, else series, else needs action). */
  response: effectiveMeetingResponseSchema,
  /** Where the answer came from; null while `needs_action`. */
  source: meetingResponseSourceSchema.nullable(),
  /** Members and invited guests only; never shown in a DM (D-1015). */
  note: z.string().nullable(),
  respondedAt: isoDateTimeSchema.nullable(),
});
export type MeetingResponseDto = z.infer<typeof meetingResponseDtoSchema>;

/** `PUT …/responses/me` → the caller's effective answer and the new tallies. */
export const myMeetingResponseSchema = z.object({
  nominalDate: localDateSchema.nullable(),
  response: effectiveMeetingResponseSchema,
  note: z.string().nullable(),
  tallies: meetingResponseTalliesSchema,
});

export const listMeetingResponsesQuerySchema = z.object({
  /** An occurrence's answers; absent = the series-level answers. */
  nominalDate: localDateSchema.optional(),
});
export const meetingResponseListResponseSchema = z.object({
  items: z.array(meetingResponseDtoSchema),
  tallies: meetingResponseTalliesSchema,
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});

export const meetingGuestListResponseSchema = z.object({
  items: z.array(meetingGuestDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});

/** `POST /meeting-series/:seriesId/guests` (organisers; calendar types; the setting; ≤ 50 in all). */
export const addMeetingGuestsRequestSchema = z.strictObject({
  guests: z.array(meetingGuestInputSchema).min(1).max(CALENDAR_LIMITS.maxGuestsPerMeeting),
});
export const addMeetingGuestsResponseSchema = z.object({
  guests: z.array(addedMeetingGuestSchema),
  convertedToMembers: z.array(convertedGuestSchema),
  mail: calendarMailStateSchema,
});

/** `PATCH /meeting-series/:seriesId/guests/:guestId` (organisers): the name or the role. */
export const updateMeetingGuestRequestSchema = z
  .strictObject({
    name: guestDisplayNameSchema.optional(),
    company: guestCompanySchema.nullable().optional(),
    role: generalGuestRoleSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'Send at least one change');
export const meetingGuestResponseSchema = z.object({ guest: meetingGuestDtoSchema });

/**
 * `DELETE /meeting-series/:seriesId/guests/:guestId`: removes the guest (a CANCEL follows, live
 * sessions end through `meeting.guest.revoked`); `anonymise=true` also deletes their data now
 * (D-1034: "Remove and delete data"; never a SQL DELETE).
 */
export const removeMeetingGuestQuerySchema = z.object({
  anonymise: queryBooleanSchema.optional(),
});

/**
 * `GET …/guests/:guestId/link` (organisers, audited `meeting.guest_link_viewed`): the personal link
 * again, recomputed (D-1012); `409 guest_link_reset_needed` after a key rotation.
 * `POST …/link/reset` makes a new one (the old link stops working).
 */
export const meetingGuestLinkResponseSchema = z.object({
  personalUrl: z.string(),
  linkVersion: z.number().int().positive(),
});

/** `GET /admin/calendar/orphaned-series` (D-1046): minimal fields only. */
export const orphanedSeriesDtoSchema = z.object({
  seriesId: uuidSchema,
  title: z.string(),
  type: meetingTypeSchema,
  chairUserId: uuidSchema,
  nextStartsAt: isoDateTimeSchema.nullable(),
  guestCount: z.number().int().nonnegative(),
});
export const listOrphanedSeriesQuerySchema = paginationQuerySchema;
export const orphanedSeriesListResponseSchema = paginated(orphanedSeriesDtoSchema);

/** `POST /admin/meeting-series/:seriesId/reassign-chair` (audited `meeting.chair_reassigned`). */
export const reassignMeetingChairRequestSchema = z.strictObject({ chairUserId: uuidSchema });
